/**
 * The Display mockup's actual figures, as a report data model instance.
 *
 * Extracted verbatim from `validate.ts` so more than one acceptance suite can bind to
 * the same numbers. These are real figures from a real report — 1,114,255 impressions,
 * 19,620 clicks, CTR 1.96% — and every invariant closes exactly:
 *
 *   mobile 12,381 + desktop 7,239                     = 19,620 clicks
 *   Σ device details                                  = 12,381 (MOBILE, not total)
 *   Σ browsers                                        = 19,620
 *   Σ creative clicks                                 = 19,620
 *
 * Creative `cr4` is PAUSED and still counted: 2,386 clicks that the legacy
 * active-only filter drops. That is D33 made concrete, and excluding it understates
 * the campaign by 12%.
 */

export const SAMPLE_REPORT: unknown = {
  dataModelVersion: 1,
  metricsVersion: 'v2',
  lifecycle: 'POSTFLIGHT',
  selection: {
    adSelection: 'delivery-in-window',
    adStatusesIncluded: ['ACTIVE', 'PAUSED'],
    adGroupSelection: 'delivery-in-window',
    creativesShownLimited: false,
    nonActiveDeliveringAds: 1,
  },
  report: { id: 'r1', version: 1, generatedAt: '2026-02-01T00:00:00Z', periodStart: '2025-01-01', periodEnd: '2025-01-31' },
  campaign: {
    id: 'c1', name: 'Client Sample Report', reportSo: 'CLIENT1', so: 'CLIENT1', ios: ['IO1'],
    clientName: 'Client Sample Report', agencyName: 'YOUR COMPANY NAME',
    // The editable "Report note" (CampaignMetaCard binds `campaign.notes`). Empty here so the sample
    // renders exactly as before, while the path still round-trips for the binding-provenance check.
    notes: '',
    startDate: '01/01/2025', endDate: '01/31/2025', channels: ['DISPLAY'], bannerAssetId: 'asset-banner',
  },
  overall: {
    delivery: { impressions: 1114255, retargetImpressions: 102988, clicks: 19620, retargetClicks: 1441, mobileClicks: 12381, desktopClicks: 7239 },
    contract: { targetImpressions: 1000000 },
    derived: { ctr: 1.962, overDeliveredImpressions: 217243, overDeliveryPct: 21.7243 },
    devices: [
      { name: 'iPhone', clicks: 7874 }, { name: 'Android', clicks: 32 }, { name: 'Samsung', clicks: 3081 },
      { name: 'iPad', clicks: 579 }, { name: 'LG', clicks: 155 }, { name: 'Htc', clicks: 14 }, { name: 'Other', clicks: 646 },
    ],
    browsers: [
      { name: 'chrome', clicks: 7246 }, { name: 'safari', clicks: 8854 }, { name: 'firefox', clicks: 422 },
      { name: 'edge', clicks: 2162 }, { name: 'other', clicks: 936 },
    ],
  },
  channels: [],
  adGroups: [{
    id: 'ag1', name: 'Display 1', channel: 'DISPLAY',
    creatives: [
      { id: 'cr1', dimensions: '728 x 90', assetId: 'creative-728x90', destinationUrl: 'https://xxxxxxx/?utm_campaign=CLIENT1&utm_source=PRODATA&utm_medium=DISPRTG',  clicks: 8118, isRepresentative: false },
      { id: 'cr2', dimensions: '160 x 600', assetId: 'creative-160x600', destinationUrl: 'https://xxxxxxx/?utm_campaign=CLIENT1&utm_source=PRODATA&utm_medium=DISPRTG', clicks: 3385, isRepresentative: false },
      { id: 'cr3', dimensions: '300 x 250', assetId: 'creative-300x250', destinationUrl: 'https://xxxxxxx/?utm_campaign=CLIENT1&utm_source=PRODATA&utm_medium=DISPRTG', clicks: 5731, isRepresentative: false },
      { id: 'cr4', dimensions: '300 x 50', assetId: 'creative-300x50', destinationUrl: 'https://xxxxxxx/?utm_campaign=CLIENT1&utm_source=PRODATA&utm_medium=DISPRTG',  clicks: 2386, isRepresentative: false, status: 'PAUSED', deliveredInWindow: true },
    ],
  }],
  targeting: {
    /*
     * All eighteen ZIPs from the mockup, not a sample of three.
     *
     * The count is load-bearing: the geographic card prints them as a wrapped list that
     * runs to two lines, and three would never exercise the wrap. A fixture that is a
     * subset of the artwork tests a layout nobody will ever see.
     */
    zips: [
      '02703', '02904', '02724', '02038', '02338', '02045', '02048', '02747', '02559',
      '02915', '02739', '02920', '02777', '02780', '02886', '02189', '02019', '02382',
    ],
    radiusMiles: 5, mapAssetId: 'asset-map',
    audience: { ageOverlay: '35-49', gender: 'Male Only', hhiThreshold: '250k Annually +', notes: ['Age Filler Overlay: 35–49', 'Male Only', 'HHI Threshold: 250k Annually +'] },
    topSites: ['yahoo.com', 'apnews.com', 'weather.com', 'nytimes.com'],
  },
  /*
   * Presentation defaults, spelled out rather than left to the schema.
   *
   * A fixture that relies on Zod's defaults renders correctly and tests nothing: the
   * flags would be absent from the object the template actually reads, so a binding
   * against one would resolve to null and the block would quietly disappear.
   *
   * The three targeting sections are OFF by default in production, but the 2026 Display mockup
   * shows them — so the sample turns them on explicitly to exercise the targeting page in the
   * fidelity and binding suites.
   */
  presentation: {
    // No white-label logo on the sample (empty → the header renders exactly as the mockup, no logo).
    logo: '',
    showLogo: true,
    showCitiesChart: false,
    showGeoTargeting: true,
    showAudienceTargeting: true,
    showTopSites: true,
  },
  provenance: { overrideCount: 0, overridesByKind: { DATA_GAP: 0, CONTRACTUAL: 0, PRESENTATION: 0 }, hasAdjustedFigures: false },
};
